import { produce } from "immer";
import * as Actions from "./composeActions";
import * as ENUMS from "./enums";

const initialState = {
  byId: {},
  allIds: [],
  // z-order, last one is on top. Kept apart from allIds because reordering
  // the rendered list would move the DOM nodes and reload TinyMCE's iframe.
  stack: [],
  activeId: null,
  limitReached: false
};

// Lays every compose out again, in opening order from the right edge (the
// newest ends up leftmost). Dragged positions are dropped on purpose: a fixed
// layout per opening is predictable and needs no overlap bookkeeping. When the
// row doesn't fit, the step shrinks so each title still shows a strip.
const tile = (draft, viewportWidth) => {
  const count = draft.allIds.length;
  let step = ENUMS.COMPOSE_WIDTH + ENUMS.COMPOSE_GAP;
  if (viewportWidth && count > 1) {
    const room = viewportWidth - ENUMS.COMPOSE_WIDTH - ENUMS.COMPOSE_GAP;
    step = Math.max(0, Math.min(step, Math.floor(room / (count - 1))));
  }
  draft.allIds.forEach((id, index) => {
    draft.byId[id].x = ENUMS.COMPOSE_GAP + index * step;
  });
};

const composeReducer = produce((draft, action) => {
  switch (action.type) {
    case Actions.OPEN_COMPOSE: {
      if (draft.allIds.length >= ENUMS.MAX_COMPOSE) {
        draft.limitReached = true;
        break;
      }

      const { id, fetchId, mode, fields, attachment_ids, viewportWidth } =
        action.payload;
      draft.byId[id] = {
        id,
        fetchId,
        title: "",
        mode,
        status: ENUMS.MAXIMIZE_COMPOSE,
        draftStatus: ENUMS.DRAFT_IDLE,
        fields,
        attachment_ids
      };
      draft.allIds.push(id);
      tile(draft, viewportWidth);
      draft.stack.push(id);
      draft.activeId = id;
      break;
    }

    case Actions.CLOSE_COMPOSE: {
      const id = action.payload;
      if (id) {
        delete draft.byId[id];
        draft.allIds = draft.allIds.filter(draftId => draftId !== id);
        draft.stack = draft.stack.filter(stackId => stackId !== id);
        if (draft.activeId === id) draft.activeId = null;
      } else {
        draft.byId = {};
        draft.allIds = [];
        draft.stack = [];
        draft.activeId = null;
      }

      break;
    }

    case Actions.SET_COMPOSE_STATUS: {
      const { id, status } = action.payload;
      draft.byId[id].status = status;
      break;
    }

    case Actions.SET_DRAFT_STATUS: {
      const { id, status } = action.payload;
      if (id in draft.byId) {
        draft.byId[id].draftStatus = status;
      }
      break;
    }

    case Actions.SET_HEADER_TITLE: {
      const { id, title } = action.payload;
      draft.byId[id].title = title;
      break;
    }

    case Actions.FOCUS_COMPOSE: {
      const id = action.payload;
      if (!(id in draft.byId)) break;
      // already on top and active: keep the state untouched
      if (draft.activeId === id && draft.stack[draft.stack.length - 1] === id)
        break;
      draft.stack = draft.stack.filter(stackId => stackId !== id);
      draft.stack.push(id);
      draft.activeId = id;
      break;
    }

    case Actions.BLUR_COMPOSE: {
      draft.activeId = null;
      break;
    }

    case Actions.SET_COMPOSE_POSITION: {
      const { id, x } = action.payload;
      if (id in draft.byId) {
        draft.byId[id].x = x;
      }
      break;
    }

    case Actions.DISMISS_COMPOSE_LIMIT: {
      draft.limitReached = false;
      break;
    }

    default: {
      return draft;
    }
  }
}, initialState);

export default composeReducer;
